import { Action } from "~/modules/actions/Action";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { getLogger, LoggerName } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { AnalyticsImpl } from "~/modules/analytics/AnalyticsImpl";
import { CbmImpl, TaskParticipant } from "~/modules/cbm";
import { getReservationsByTask, sendTrackingEvent } from "~/modules/actions/ActionUtils";
import { EVENTS } from "~/modules/analytics/Analytics";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { AccountConfigDataContainer } from "~/modules/config/AccountConfig/AccountConfigImpl/AccountConfigDataContainer/AccountConfigDataContainer";




export interface StartChannelTransferOptions {
    notesSid?: string;
    type?: "warm" | "cold";
}
























export class StartChannelTransfer implements Action<Promise<void>> {
    


    #taskSid: string;

    


    #to: string;

    


    #options?: StartChannelTransferOptions;

    constructor(taskSid: string, to: string, options?: StartChannelTransferOptions) {
        this.#taskSid = taskSid;
        this.#to = to;
        this.#options = options;
    }

    
    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const logger = getLogger(context)(LoggerName.Actions);

        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const worker = taskRouter.worker;

        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        const accountConfigDataContainer = context.retrieveInstanceOf(
            ContextInstance.AccountConfigDataContainer,
            AccountConfigDataContainer
        );

        logger.debug(`startChannelTransfer invoked with taskSid: ${this.#taskSid}`);

        const task = getReservationsByTask(worker, this.#taskSid)[0]?.task;

        if (!task) {
            const errorMsg = `startChannelTransfer: Task not found for taskSid ${this.#taskSid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartChannelTransfer, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                to: this.#to,
                notesSid: this.#options?.notesSid,
                transferType: this.#options?.type
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const interactionSid = task.attributes.flexInteractionSid;

        if (!interactionSid) {
            const errorMsg = `startChannelTransfer: Missing interactionSid for taskSid ${this.#taskSid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartChannelTransfer, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                to: this.#to,
                notesSid: this.#options?.notesSid,
                transferType: this.#options?.type
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const channelSid = task.attributes.flexInteractionChannelSid;

        if (!channelSid) {
            const errorMsg = `startChannelTransfer: Missing channelSid for taskSid ${this.#taskSid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartChannelTransfer, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                to: this.#to,
                notesSid: this.#options?.notesSid,
                transferType: this.#options?.type,
                interactionSid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const instanceSid = accountConfigDataContainer.get().flexInstanceSid;

        if (!instanceSid) {
            const errorMsg = `startChannelTransfer: Missing instanceSid for taskSid ${this.#taskSid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartChannelTransfer, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                to: this.#to,
                notesSid: this.#options?.notesSid,
                transferType: this.#options?.type,
                interactionSid,
                channelSid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        let workerParticipant: TaskParticipant | undefined;
        try {
            const participants = await cbm.getParticipantsByTask(task);
            workerParticipant = participants.find(
                (p) =>
                    (p.type === "agent" || p.type === "supervisor") &&
                    p.routingProperties?.workerSid === worker.workerSid
            );
        } catch (e) {
            const errorMsg = `startChannelTransfer: Failed to get participants for taskSid ${this.#taskSid}`;
            logger.error(errorMsg, e);
            sendTrackingEvent(EVENTS.StartChannelTransfer, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                to: this.#to,
                notesSid: this.#options?.notesSid,
                transferType: this.#options?.type,
                interactionSid,
                channelSid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (!workerParticipant) {
            const errorMsg = `startChannelTransfer: Worker participant not found for taskSid ${this.#taskSid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartChannelTransfer, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                to: this.#to,
                notesSid: this.#options?.notesSid,
                transferType: this.#options?.type,
                interactionSid,
                channelSid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        try {
            await cbm.transferChannel(
                instanceSid,
                interactionSid,
                channelSid,
                workerParticipant.participantSid,
                this.#to,
                this.#options?.notesSid,
                this.#options?.type
            );

            sendTrackingEvent(EVENTS.StartChannelTransfer, analytics, worker, {
                taskSid: this.#taskSid,
                to: this.#to,
                notesSid: this.#options?.notesSid,
                transferType: this.#options?.type,
                interactionSid,
                channelSid,
                workerParticipantSid: workerParticipant.participantSid
            });

            return Promise.resolve();
        } catch (e) {
            const errorMsg = `startChannelTransfer: Failed to transfer conversation channel for taskSid ${
                this.#taskSid
            } : ${e.message}`;
            if (String(e.message.toLowerCase()).includes("forbidden")) {
                const customErrorMsg = `${errorMsg}: Check if messaging transfers are enabled in Twilio Console`;
                logger.error(customErrorMsg);
                sendTrackingEvent(EVENTS.StartChannelTransfer, analytics, worker, {
                    error: customErrorMsg,
                    taskSid: this.#taskSid,
                    to: this.#to,
                    notesSid: this.#options?.notesSid,
                    transferType: this.#options?.type,
                    interactionSid,
                    channelSid,
                    workerParticipantSid: workerParticipant.participantSid
                });
                return Promise.reject(
                    new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, customErrorMsg)
                );
            }

            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartChannelTransfer, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                to: this.#to,
                notesSid: this.#options?.notesSid,
                transferType: this.#options?.type,
                interactionSid,
                channelSid,
                workerParticipantSid: workerParticipant.participantSid
            });

            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }
    }
}
